import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Trash2, Plus } from "lucide-react";

const TIPI = [["ferie", "Ferie"], ["malattia", "Malattia"], ["infortunio", "Infortunio"], ["permesso", "Permesso"], ["altro", "Altro"]];
const fmt = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : "da definire");

// Iter 106: assenze programmate (ferie/malattia/infortunio) con date → spostano da sole le persone
export default function AssenzeDialog({ open, onClose, persone, isAdmin, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ persona: "", tipo: "ferie", dal: "", al: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/assenze"); setItems(data || []); }
    catch { toast.error("Errore caricamento assenze"); } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const options = useMemo(() => {
    const s = q.toLowerCase();
    return persone.filter((p) => !s || p.label.toLowerCase().includes(s)).slice(0, 40);
  }, [persone, q]);

  const save = async () => {
    if (!form.persona || !form.dal) return toast.error("Scegli la persona e la data di inizio");
    const [persona_tipo, persona_id] = form.persona.split("|");
    setSaving(true);
    try {
      await api.post("/assenze", { persona_tipo, persona_id, tipo: form.tipo, dal: form.dal, al: form.al, note: form.note });
      toast.success("Assenza registrata");
      setForm({ persona: "", tipo: "ferie", dal: "", al: "", note: "" });
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    try { await api.delete(`/assenze/${a.id}`); await load(); onChanged?.(); } catch { toast.error("Errore"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const attive = items.filter((a) => !a.al || a.al >= today);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="assenze-dialog">
        <DialogHeader><DialogTitle className="font-heading">Assenze programmate</DialogTitle></DialogHeader>
        {isAdmin && (
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end rounded-xl border bg-slate-50 p-3">
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold">Persona</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="h-9 mt-1" data-testid="assenza-search" />
              <Select value={form.persona} onValueChange={(v) => setForm({ ...form, persona: v })}>
                <SelectTrigger className="h-9 mt-1" data-testid="assenza-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{options.map((p) => <SelectItem key={`${p.tipo}|${p.id}`} value={`${p.tipo}|${p.id}`}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="h-9 mt-1" data-testid="assenza-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-bold">Dal</Label><Input type="date" value={form.dal} onChange={(e) => setForm({ ...form, dal: e.target.value })} className="h-9 mt-1" data-testid="assenza-dal" /></div>
            <div><Label className="text-xs font-bold">Al (opzionale)</Label><Input type="date" value={form.al} onChange={(e) => setForm({ ...form, al: e.target.value })} className="h-9 mt-1" data-testid="assenza-al" /></div>
            <Button onClick={save} disabled={saving} className="h-9 bg-[#0044FF] hover:bg-[#0033CC]" data-testid="assenza-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}Aggiungi</Button>
            <div className="sm:col-span-6"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Nota (es. rientra sabato sera)" className="h-9" data-testid="assenza-note" /></div>
          </div>
        )}
        {loading ? <div className="py-6 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <div className="divide-y rounded-xl border" data-testid="assenze-list">
            {attive.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-400">Nessuna assenza in corso o futura</div>}
            {attive.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`assenza-row-${a.id}`}>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${a.tipo === "ferie" ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-700"}`}>{TIPI.find(([v]) => v === a.tipo)?.[1] || a.tipo}</span>
                <span className="font-semibold flex-1 truncate">{a.persona_nome}</span>
                <span className="text-slate-600 font-mono text-xs">{fmt(a.dal)} → {fmt(a.al)}</span>
                {a.note && <span className="text-xs text-slate-500 truncate max-w-[180px]">{a.note}</span>}
                {isAdmin && <button onClick={() => remove(a)} className="p-1 rounded text-red-600 hover:bg-red-50" data-testid={`assenza-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
